<template>
  <article
    class="wa-mobile-card"
    :class="{ 'wa-mobile-card--active': isActive, 'wa-mobile-card--inactive': !isActive }"
  >
    <header class="wa-mobile-card__header">
      <div class="wa-mobile-card__title-row">
        <Checkbox class="wa-mobile-card__check" :value="selected" @input="emitSelect" />
        <h3 class="wa-area-name" dir="auto">{{ workingArea.area_name }}</h3>
        <span class="wa-status-pill" :class="isActive ? 'wa-status-pill--on' : 'wa-status-pill--off'">
          {{ isActive ? $t('active') : $t('inactive') }}
        </span>
      </div>
      <div v-if="isAdmin" class="wa-mobile-card__header-actions">
        <button type="button" class="wa-icon-btn" :title="$t('edit')" @click="emitEdit">
          <img src="@/assets/survey/edit-selected.svg" alt="" />
        </button>
        <button type="button" class="wa-icon-btn wa-icon-btn--danger" :title="$t('remove')" @click="emitRemove">
          <img src="@/assets/delete-icon.svg" alt="" />
        </button>
      </div>
    </header>

    <dl class="wa-mobile-card__metrics">
      <div class="wa-mobile-card__metric">
        <dt class="wa-mobile-card__label">{{ $t('rooms') }}</dt>
        <dd class="wa-mobile-card__value">
          <button
            type="button"
            class="wa-link-btn"
            :disabled="!workingArea.rooms_number"
            @click="openListModal('rooms')"
          >
            {{ workingArea.rooms_number }}
          </button>
        </dd>
      </div>

      <div class="wa-mobile-card__metric wa-mobile-card__metric--full">
        <dt class="wa-mobile-card__label">{{ $t('shifts') || 'Shifts' }}</dt>
        <dd class="wa-mobile-card__value">
          <button
            v-if="workingArea.shifts && workingArea.shifts.length"
            type="button"
            class="wa-shifts-btn"
            @click="openListModal('shifts')"
          >
            <span class="wa-shifts-text" dir="auto">{{ shiftsPreview }}</span>
            <span v-if="hiddenShiftsCount" class="wa-shifts-more">+{{ hiddenShiftsCount }}</span>
          </button>
          <span v-else class="wa-muted">—</span>
        </dd>
      </div>

      <div class="wa-mobile-card__metric wa-mobile-card__metric--full">
        <dt class="wa-mobile-card__label">{{ $t('working_area_alerts') }}</dt>
        <dd class="wa-mobile-card__value">
          <WorkingAreaAlertFlags :working-area="workingArea" />
        </dd>
      </div>
    </dl>
  </article>
</template>

<script>
import Checkbox from '@/modules/common/components/Checkbox'
import WorkingAreaAlertFlags from './WorkingAreaAlertFlags.vue'
import { isWorkingAreaActive } from '../utils/workingAreaStatus'

const SHIFTS_PREVIEW_MAX = 2

export default {
  props: {
    workingArea: {
      type: Object,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isActive() {
      return isWorkingAreaActive(this.workingArea)
    },
    shiftsPreview() {
      const shifts = this.workingArea.shifts || []
      if (!shifts.length) return ''
      return shifts.slice(0, SHIFTS_PREVIEW_MAX).join(', ')
    },
    hiddenShiftsCount() {
      const shifts = this.workingArea.shifts || []
      return shifts.length > SHIFTS_PREVIEW_MAX ? shifts.length - SHIFTS_PREVIEW_MAX : 0
    },
  },
  methods: {
    openListModal(type) {
      const items = type === 'rooms'
        ? (this.workingArea.rooms_names || [])
        : (this.workingArea.shifts || [])
      this.$emit('openListModal', {
        type,
        titleKey: type === 'rooms' ? 'rooms' : 'shifts',
        subtitle: this.workingArea.area_name,
        items,
      })
    },
    emitSelect(value) {
      this.$emit('select', this.workingArea.id, !!value)
    },
    emitEdit() {
      this.$emit('editWorkingArea', this.workingArea.id)
    },
    emitRemove() {
      this.$emit('removeWorkingArea', this.workingArea.id)
    },
  },
  components: {
    Checkbox,
    WorkingAreaAlertFlags,
  },
}
</script>

<style lang="scss" scoped>
.wa-mobile-card {
  background: #fff;
  border: 1px solid #e6eaef;
  border-radius: 10px;
  padding: 10px 12px;

  &--active {
    border-color: #b8dfc8;
    background: #f7fbf8;
  }

  &--inactive {
    border-color: #e6eaef;
    background: #fafbfc;
  }
}

.wa-mobile-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef1f4;
}

.wa-mobile-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.wa-mobile-card__check {
  flex-shrink: 0;
}

.wa-area-name {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2a37;
  line-height: 1.3;
  word-break: break-word;
}

.wa-mobile-card__header-actions {
  display: flex;
  flex-shrink: 0;
  gap: 2px;
}

.wa-mobile-card__metrics {
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}

.wa-mobile-card__metric {
  margin: 0;
  min-width: 0;

  &--full {
    grid-column: 1 / -1;
  }
}

.wa-mobile-card__label {
  margin: 0 0 2px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a939e;
  line-height: 1.2;
}

.wa-mobile-card__value {
  margin: 0;
  font-size: 13px;
  color: #3a4652;
  line-height: 1.3;
}
</style>
